import styled from "styled-components";

export type AnalyticsTone = "blue" | "cyan" | "green" | "gold" | "red" | "purple";

const toneColor = (tone?: AnalyticsTone) => {
  if (tone === "cyan") return "#0891b2";
  if (tone === "green") return "#16a34a";
  if (tone === "gold") return "#d97706";
  if (tone === "red") return "#dc2626";
  if (tone === "purple") return "#7c3aed";
  return "#2563eb";
};

export const PageWrap = styled.div`
  min-width: 0;
  min-height: 100%;
  padding: 14px 16px 20px;
  overflow-x: hidden;
  background: #f4f6f9;

  @media (max-width: 760px) {
    padding: 8px 8px 16px;
  }
`;

export const StickyControls = styled.div`
  position: sticky;
  top: 0;
  z-index: 20;
  padding-bottom: 10px;
  background: #f4f6f9;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  min-height: 42px;
  margin-bottom: 8px;

  @media (max-width: 900px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const HeaderCopy = styled.div`
  min-width: 0;
`;

export const HeaderActions = styled.div`
  display: flex;
  flex: 0 0 auto;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;

  @media (max-width: 760px) {
    width: 100%;
    justify-content: flex-start;
  }
`;

export const FilterPanel = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 10px;
  background: #fff;
  border: 1px solid #dfe5ec;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgb(15 23 42 / 4%);

  .ant-input,
  .ant-input-number,
  .ant-select-selector {
    border-color: #d8dee8 !important;
  }

  @media (max-width: 760px) {
    > .ant-select,
    > .ant-input,
    > .ant-input-affix-wrapper,
    > .ant-input-number {
      width: 100% !important;
      min-width: 0 !important;
    }
  }
`;

export const KpiGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: 10px;

  @media (max-width: 1500px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 760px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const KpiCard = styled.div<{ $tone?: AnalyticsTone }>`
  min-width: 0;
  min-height: 94px;
  padding: 13px 14px 11px;
  background: #fff;
  border: 1px solid #dfe5ec;
  border-top: 3px solid ${(props) => toneColor(props.$tone)};
  border-radius: 8px;
`;

export const KpiLabel = styled.div`
  color: #64748b;
  font-size: 12px;
  font-weight: 500;
`;

export const KpiValue = styled.div`
  margin-top: 3px;
  color: #111827;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
`;

export const MetricHint = styled.div`
  min-height: 18px;
  margin-top: 4px;
  color: #64748b;
  font-size: 12px;
`;

export const TabsSurface = styled.div`
  min-width: 0;
  margin-top: 12px;

  > .ant-tabs > .ant-tabs-nav {
    margin-bottom: 12px;
    padding: 0 10px;
    background: #fff;
    border: 1px solid #dfe5ec;
    border-radius: 8px;
  }

  .ant-tabs-content-holder,
  .ant-tabs-tabpane {
    min-width: 0;
  }
`;

export const DashboardStack = styled.div`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 12px;
`;

export const OverviewTopGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.55fr) minmax(320px, 0.8fr);
  gap: 12px;

  @media (max-width: 1280px) {
    grid-template-columns: 1fr;
  }
`;

export const InsightGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1450px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

export const OverviewBottomGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(340px, 0.75fr);
  gap: 12px;

  @media (max-width: 1280px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.section`
  min-width: 0;
  height: 100%;
  padding: 13px 14px;
  background: #fff;
  border: 1px solid #dfe5ec;
  border-radius: 8px;
`;

export const PanelHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  min-height: 24px;
  margin-bottom: 12px;
`;

export const PanelTitle = styled.div`
  overflow: hidden;
  color: #111827;
  font-size: 14px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FunnelLine = styled.div`
  display: grid;
  grid-template-columns: 28px minmax(155px, 1.15fr) minmax(150px, 1fr) 126px;
  gap: 10px;
  align-items: center;
  min-height: 56px;
  padding: 7px 0;
  border-bottom: 1px solid #edf1f5;

  &:last-child {
    border-bottom: 0;
  }

  @media (max-width: 760px) {
    grid-template-columns: 28px minmax(0, 1fr);
  }
`;

export const StepIndex = styled.div`
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #475569;
  background: #eef2f7;
  border: 1px solid #dbe3ec;
  border-radius: 50%;
  font-size: 12px;
`;

export const StepName = styled.div`
  min-width: 0;
  overflow: hidden;
  color: #111827;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Muted = styled.div`
  overflow: hidden;
  color: #64748b;
  font-size: 12px;
  line-height: 1.45;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FunnelVisual = styled.div`
  position: relative;
  height: 24px;
  overflow: hidden;
  background: #eef2f7;
  border-radius: 5px;

  @media (max-width: 760px) {
    grid-column: 2;
  }
`;

export const FunnelReach = styled.div<{ $percent: number; $tone?: "ok" | "warn" | "danger" }>`
  width: ${(props) => Math.max(3, Math.min(100, props.$percent))}%;
  height: 100%;
  background: ${(props) => props.$tone === "danger"
    ? "#ef4444"
    : props.$tone === "warn"
      ? "#f59e0b"
      : "#0f766e"};
  opacity: 0.82;
`;

export const FunnelOutcome = styled.div`
  min-width: 0;

  @media (max-width: 760px) {
    grid-column: 2;
  }
`;

export const FunnelRate = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #111827;
  font-size: 13px;
  font-weight: 650;
`;

export const ConversionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
`;

export const ConversionMetric = styled.div`
  min-width: 0;
  padding: 11px;
  background: #f8fafc;
  border: 1px solid #e7edf3;
  border-radius: 6px;
`;

export const ConversionLabel = styled.div`
  margin-bottom: 5px;
  color: #64748b;
  font-size: 12px;
`;

export const ConversionValue = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 5px;
  color: #111827;
  font-size: 18px;
  font-weight: 700;
`;

export const RiskLine = styled.div`
  display: grid;
  grid-template-columns: minmax(130px, 1.25fr) minmax(90px, 0.8fr) auto;
  gap: 9px;
  align-items: center;
  min-height: 48px;
  padding: 7px 0;
  border-bottom: 1px solid #edf1f5;

  &:last-child {
    border-bottom: 0;
  }
`;

export const MiniBar = styled.div`
  height: 7px;
  overflow: hidden;
  background: #e5eaf0;
  border-radius: 999px;
`;

export const MiniBarFill = styled.div<{ $percent: number; $tone?: "ok" | "warn" | "danger" }>`
  width: ${(props) => Math.max(0, Math.min(100, props.$percent))}%;
  height: 100%;
  background: ${(props) => props.$tone === "danger" ? "#dc2626" : props.$tone === "warn" ? "#d97706" : "#0f766e"};
`;

export const TrendLegend = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
  margin: -4px 0 6px;
  color: #64748b;
  font-size: 12px;
`;

export const TrendLegendItem = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;

  &::before {
    width: 8px;
    height: 8px;
    background: ${(props) => props.$color};
    border-radius: 2px;
    content: "";
  }
`;

export const TrendLine = styled.div`
  display: grid;
  grid-template-columns: 88px minmax(150px, 1fr) repeat(4, 64px);
  gap: 8px;
  align-items: center;
  min-height: 37px;
  border-bottom: 1px solid #edf1f5;

  &:last-child {
    border-bottom: 0;
  }
`;

export const TrendBars = styled.div`
  display: grid;
  gap: 2px;
`;

export const TrendTrack = styled.div`
  height: 3px;
  overflow: hidden;
  background: #edf1f5;
  border-radius: 999px;
`;

export const TrendFill = styled.div<{ $percent: number; $color: string }>`
  width: ${(props) => Math.max(0, Math.min(100, props.$percent))}%;
  height: 100%;
  background: ${(props) => props.$color};
`;

export const TableSurface = styled.div`
  min-width: 0;
  padding: 10px;
  background: #fff;
  border: 1px solid #dfe5ec;
  border-radius: 8px;

  .ant-table-wrapper {
    min-width: 0;
  }
`;
